import { useCallback } from 'react'
import { Path, useLocation, useNavigate } from 'react-router-dom'
import log from 'loglevel'
import { VendorPromiseClient } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_grpc_web_pb'
import {
  CancelVendorEvaluationRequest,
  ListVendorEvaluationsRequest,
  ListVendorEvaluationsResponse,
  StartVendorEvaluationRequest,
  VendorEvaluationCycle,
  VendorEvaluationCycleStatus,
} from '@trustero/trustero-api-web/lib/vendormanagement/vendor_evaluation_cycle_pb'
import { useAuthorizedGrpcClient } from 'src/adapter'
import { useGrpcRevalidateByMethod } from 'src/components'
import {
  AuthorizedSwrResponse,
  useSwrGrpc,
} from 'src/components/async/useSwrGrpc'
import { withSearchParam } from 'src/components/Reusable/Tabs/useShowPageTab'

export const EVALUATION_PAGE_SIZE = 20

// `?evaluationId=<cycle id>` on a vendor page: that evaluation's chat is open
// in the copilot. The URL leads (EvaluationLinkSync), so a copied or shared
// link opens the same evaluation; the evaluation-complete notification links
// here too (ntrced/notification/vendor_evaluation_completed_builder.go).
export const EVALUATION_ID_PARAM = 'evaluationId'

// Where an evaluation opens: this page with it named in the URL, which
// EvaluationLinkSync turns into its chat in the copilot.
export const useEvaluationLink = (): ((cycleId: string) => Partial<Path>) => {
  const { search, hash } = useLocation()
  return useCallback(
    (cycleId: string) => ({
      search: withSearchParam(search, EVALUATION_ID_PARAM, cycleId),
      hash,
    }),
    [search, hash],
  )
}

// Opens an evaluation from code (Evaluate Vendor). A push, like any click:
// Back closes it again.
export const useOpenEvaluation = (): ((cycleId: string) => void) => {
  const evaluationLink = useEvaluationLink()
  const { state } = useLocation()
  const navigate = useNavigate()
  return useCallback(
    (cycleId: string) => navigate(evaluationLink(cycleId), { state }),
    [navigate, evaluationLink, state],
  )
}

// An evaluation cycle is open until it is completed or cancelled; while it
// is open the button reads "Cancel evaluation".
export const isOpenEvaluation = (
  status: VendorEvaluationCycleStatus,
): boolean =>
  status !==
    VendorEvaluationCycleStatus.VENDOR_EVALUATION_CYCLE_STATUS_COMPLETED &&
  status !==
    VendorEvaluationCycleStatus.VENDOR_EVALUATION_CYCLE_STATUS_CANCELLED &&
  status !==
    VendorEvaluationCycleStatus.VENDOR_EVALUATION_CYCLE_STATUS_UNSPECIFIED

// Only a page with an open cycle has anything to poll for: its status moves
// as the agent works. A history of closed cycles does not change on its own.
export const OPEN_EVALUATION_POLL_MS = 15000

export const hasOpenEvaluation = (
  latest?: ListVendorEvaluationsResponse,
): boolean =>
  !!latest?.getCyclesList().some((cycle) => isOpenEvaluation(cycle.getStatus()))

export const useVendorEvaluations = (
  vendorId: string,
  page: number,
  shouldFetch = true,
): AuthorizedSwrResponse<ListVendorEvaluationsResponse> => {
  const { response } = useSwrGrpc(
    VendorPromiseClient.prototype.listVendorEvaluations,
    new ListVendorEvaluationsRequest()
      .setVendorId(vendorId)
      .setPageNumber(page)
      .setPageSize(EVALUATION_PAGE_SIZE),
    !!vendorId && shouldFetch,
    {
      // Inline on purpose: SWR re-arms the interval when the function's
      // identity changes, so a hoisted constant would evaluate once and
      // never again.
      refreshInterval: (latest?: ListVendorEvaluationsResponse) =>
        hasOpenEvaluation(latest) ? OPEN_EVALUATION_POLL_MS : 0,
    },
  )
  return response
}

// The vendor's one evaluation with this id, or held in this chat, wherever it
// is in the history. Undefined while loading or when asked for nothing; null
// when the vendor has no such evaluation.
export const useVendorEvaluationLookup = (
  vendorId: string,
  { cycleId = '', chatId = '' }: { cycleId?: string; chatId?: string },
): VendorEvaluationCycle | null | undefined => {
  const { response } = useSwrGrpc(
    VendorPromiseClient.prototype.listVendorEvaluations,
    new ListVendorEvaluationsRequest()
      .setVendorId(vendorId)
      .setCycleId(cycleId)
      .setChatId(chatId)
      .setPageSize(1),
    !!vendorId && !!(cycleId || chatId),
  )
  const data = response.data
  return data ? data.getCyclesList()[0] ?? null : undefined
}

const useRefreshEvaluations = (): (() => Promise<void>) => {
  const mutateFunc = useGrpcRevalidateByMethod()
  return useCallback(async () => {
    try {
      await mutateFunc(VendorPromiseClient.prototype.listVendorEvaluations)
    } catch (error) {
      log.error('Error refreshing vendor evaluations', error)
    }
  }, [mutateFunc])
}

// Resolves to the new cycle's id, so the caller can open it at once.
export const useStartVendorEvaluation = (): ((
  vendorId: string,
) => Promise<string>) => {
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const refresh = useRefreshEvaluations()
  return useCallback(
    async (vendorId: string) => {
      try {
        const cycle = await client.startVendorEvaluation(
          new StartVendorEvaluationRequest().setVendorId(vendorId),
        )
        return cycle.getId()
      } catch (error) {
        log.error('Error starting vendor evaluation', error)
        throw error
      } finally {
        await refresh()
      }
    },
    [client, refresh],
  )
}

export const useCancelVendorEvaluation = (): ((
  cycleId: string,
) => Promise<void>) => {
  const client = useAuthorizedGrpcClient(VendorPromiseClient)
  const refresh = useRefreshEvaluations()
  return useCallback(
    async (cycleId: string) => {
      try {
        await client.cancelVendorEvaluation(
          new CancelVendorEvaluationRequest().setCycleId(cycleId),
        )
      } catch (error) {
        log.error('Error cancelling vendor evaluation', error)
        throw error
      } finally {
        await refresh()
      }
    },
    [client, refresh],
  )
}
